import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  useMediaQuery,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Card,
  CardContent,
  useTheme,
  List,
  ListItem,
  Divider,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { SpinHistory } from '../types';
import { getUserSpinHistory, claimSpinReward } from '../services/api';
import { colors } from '../theme/themeConfig';
import PageSwitcher from '../components/PageSwitcher';
// Import UserContext
import { useUser } from '../contexts/UserContext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const SpinHistoryPage: React.FC = () => {
  // Use UserContext instead of local state
  const { user, telegramUser, isLoading: userLoading } = useUser();
  
  const [history, setHistory] = useState<SpinHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadStartTime] = useState(Date.now());
  const [claiming, setClaiming] = useState<string | null>(null);
  
  // States for UC activation dialog
  const [showUcActivationDialog, setShowUcActivationDialog] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<SpinHistory | null>(null);
  const [gameId, setGameId] = useState('');
  const [gameIdError, setGameIdError] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // States for result dialogs
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  
  // Using single breakpoint at 375px as per user preference
  const isMobile = useMediaQuery('(max-width: 375px)');
  const navigate = useNavigate();
  const username = telegramUser?.username || user?.telegramUsername || 'ggk_1';

  // Add effect to handle loading timeout
  useEffect(() => {
    
    // Add a timeout to prevent infinite loading
    const timer = setTimeout(() => {
      if (loading && (Date.now() - loadStartTime) > 5000) {
        setLoading(false);
      }
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [userLoading, loading, loadStartTime]);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        setLoading(true);
        
        // Only proceed if we have the telegramUser from context
        if (telegramUser) {
          // Get spin history
          const historyData = await getUserSpinHistory();
          setHistory(historyData);
        }
      } catch (error) {
        console.error('Error loading history:', error);
      } finally {
        setLoading(false);
      }
    };
    
    // Only load history when telegramUser is available
    if (telegramUser) {
      loadHistory();
    }
  }, [telegramUser]);

  const handleClaimReward = async (historyItem: SpinHistory) => {
    // Different handling based on reward type
    if (historyItem.rewardType === 'uc') {
      // For UC rewards, show the activation dialog
      setSelectedHistory(historyItem);
      setGameId('');
      setGameIdError('');
      setShowUcActivationDialog(true);
    } else {
      // For other rewards, handle accordingly
      try {
        setClaiming(historyItem.id);
        const result = await claimSpinReward(historyItem.id);
        
        if (result.success) {
          setResultMessage(result.message || 'Награда успешно активирована!');
          setShowSuccessDialog(true);
          
          // Refresh history after claiming
          if (telegramUser) {
            const historyData = await getUserSpinHistory();
            setHistory(historyData);
          }
        } else {
          setResultMessage(result.message || 'Не удалось активировать награду');
          setShowErrorDialog(true);
        }
      } catch (error) {
        console.error('Error claiming reward:', error);
        setResultMessage('Произошла ошибка при активации награды');
        setShowErrorDialog(true);
      } finally {
        setClaiming(null);
      }
    }
  };

  const validateGameId = (id: string): boolean => {
    if (!id.trim()) {
      setGameIdError('Введите game ID');
      return false;
    }
    
    if (!/^\d{6,12}$/.test(id)) {
      setGameIdError('Game ID должен содержать от 6 до 12 цифр');
      return false;
    }
    
    setGameIdError('');
    return true;
  };

  const handleActivateUc = () => {
    if (!validateGameId(gameId) || !selectedHistory) {
      return;
    }
    
    // Show confirmation dialog
    setShowConfirmation(true);
  };

  const handleConfirmActivation = async () => {
    if (!selectedHistory) return;
    
    try {
      setClaiming(selectedHistory.id);
      setShowConfirmation(false);
      
      // Call API to claim reward with game ID
      const result = await claimSpinReward(selectedHistory.id, gameId);
      
      if (result.success) {
        // Update local state
        setHistory(prev => prev.map(item => 
          item.id === selectedHistory.id ? { ...item, claimed: true } : item
        ));
        
        // Close the activation dialog
        setShowUcActivationDialog(false);
        
        // Show success dialog
        setResultMessage(result.message || `${selectedHistory.amount} UC успешно активированы!`);
        setShowSuccessDialog(true);
      } else {
        // Show error dialog
        setResultMessage(result.message || 'Не удалось активировать UC. Попробуйте позже.');
        setShowErrorDialog(true);
      }
    } catch (error) {
      console.error('Error claiming UC reward:', error);
      setResultMessage('Не удалось активировать UC. Попробуйте позже.');
      setShowErrorDialog(true);
    } finally {
      setClaiming(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderUcActivationDialog = () => {
    if (!showUcActivationDialog || !selectedHistory) return null;
    
    return (
      <Dialog 
        open={showUcActivationDialog} 
        onClose={() => setShowUcActivationDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: `1px solid ${colors.border.light}`,
          pb: 1
        }}>
          <Typography variant="h6">Активация UC</Typography>
          <IconButton 
            edge="end" 
            color="inherit" 
            onClick={() => setShowUcActivationDialog(false)}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Вы активируете <strong>{selectedHistory.amount} UC</strong>
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 2, color: colors.text.secondary }}>
            Для активации введите ID вашего аккаунта в игре PUBG Mobile:
          </Typography>
          
          <TextField
            label="Game ID"
            variant="outlined"
            fullWidth
            value={gameId}
            onChange={(e) => {
              setGameId(e.target.value);
              if (e.target.value) validateGameId(e.target.value);
            }}
            error={!!gameIdError}
            helperText={gameIdError}
            sx={{ mb: 2 }}
            placeholder="Например: 5286954721"
          />
          
          <Typography variant="caption" sx={{ color: colors.text.secondary, display: 'block', mb: 1 }}>
            Game ID можно найти в профиле вашего аккаунта PUBG Mobile.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            variant="contained" 
            color="primary"
            fullWidth
            disabled={!gameId || !!gameIdError}
            onClick={handleActivateUc}
          >
            Активировать
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderConfirmationDialog = () => {
    if (!showConfirmation || !selectedHistory) return null;
    
    return (
      <Dialog 
        open={showConfirmation} 
        onClose={() => setShowConfirmation(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Подтверждение активации</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Вы уверены, что хотите активировать <strong>{selectedHistory.amount} UC</strong> для аккаунта с ID:
          </Typography>
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 2 }}>
            {gameId}
          </Typography>
          <Typography variant="body2" sx={{ color: 'warning.main' }}>
            Внимание! После активации изменить game ID будет невозможно.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmation(false)} color="inherit">
            Назад
          </Button>
          <Button 
            onClick={handleConfirmActivation} 
            color="primary" 
            variant="contained"
            disabled={claiming === selectedHistory.id}
          >
            {claiming === selectedHistory.id ? 'Активация...' : 'Подтвердить'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderResultDialog = () => {
    if (showSuccessDialog) {
      return (
        <Dialog 
          open={showSuccessDialog} 
          onClose={() => setShowSuccessDialog(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogContent sx={{ textAlign: 'center', pt: 4 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 60, mb: 2, color: '#4caf50' }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Успешно!
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {resultMessage}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button 
              onClick={() => setShowSuccessDialog(false)} 
              color="primary" 
              variant="contained"
            >
              Закрыть
            </Button>
          </DialogActions>
        </Dialog>
      );
    }
    
    if (showErrorDialog) {
      return (
        <Dialog 
          open={showErrorDialog} 
          onClose={() => setShowErrorDialog(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogContent sx={{ textAlign: 'center', pt: 4 }}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              Ошибка
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {resultMessage}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button 
              onClick={() => setShowErrorDialog(false)} 
              color="primary" 
              variant="contained"
            >
              Закрыть
            </Button>
          </DialogActions>
        </Dialog>
      );
    }
    
    return null;
  };

  const isLoading = loading || userLoading;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: colors.background.default,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        padding: isMobile ? '16px' : '24px',
        backgroundColor: colors.background.default,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflowX: 'hidden',
        paddingBottom: '80px',
      }}
    >
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          width: '100%', 
          maxWidth: '400px',
          marginBottom: '16px'
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/paid-roulette')}
          sx={{ 
            color: colors.text.primary,
            textTransform: 'none',
            padding: '8px',
            minWidth: 'auto',
          }}
        >
          Назад
        </Button>
        <Typography 
          variant="h6" 
          sx={{ 
            flexGrow: 1, 
            textAlign: 'center',
            fontWeight: 500,
            color: colors.text.primary,
          }}
        >
          Мои призы
        </Typography>
        <Box sx={{ width: '40px' }} /> {/* Spacer for balance */}
      </Box>

      {history.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '50vh',
            textAlign: 'center',
            padding: '0 24px',
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, color: colors.text.primary }}>
            У вас пока нет призов
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: colors.text.secondary }}>
            Попробуйте покрутить рулетку и выиграть
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/paid-roulette')}
            sx={{
              borderRadius: '8px',
              padding: '10px 20px',
              textTransform: 'none',
              fontSize: '16px'
            }}
          >
            Крутить рулетку
          </Button>
        </Box>
      ) : (
        <List 
          sx={{ 
            width: '100%', 
            maxWidth: '400px',
            bgcolor: 'background.paper',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          {history.map((item, index) => (
            <React.Fragment key={item.id}>
              <ListItem
                sx={{
                  padding: '16px',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {item.rewardType === 'uc' ? `${item.amount} UC`: `${item.amount} рефбаллов`}
                  </Typography>
                  <Chip 
                    label={item.claimed ? "Активировано" : "Не активировано"} 
                    size="small"
                    color={item.claimed ? "success" : "default"}
                    sx={{ fontSize: '12px' }}
                  />
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ marginBottom: '12px' }}>
                  {formatDate(item.createdAt)}
                </Typography>
                
                {!item.claimed && (
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={claiming === item.id}
                    onClick={() => handleClaimReward(item)}
                    sx={{
                      borderRadius: '8px',
                      padding: '8px',
                      textTransform: 'none',
                      fontSize: '14px'
                    }}
                  >
                    {claiming === item.id ? 'Активация...' : 'Активировать'}
                  </Button>
                )}
              </ListItem>
              {index < history.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}
      
      {renderUcActivationDialog()}
      {renderConfirmationDialog()}
      {renderResultDialog()}
    </Box>
  );
};

export default SpinHistoryPage;
